import { useCallback, useEffect, useState } from "react";
import ConditionalRender from "../../../../../ConditionalRender";
import { apiAgent } from "../../../../../axiosConfig";
import { InputBox } from "../../../../../design_system/components/Input Fields/InputBox.jsx";
import { Loader } from "../../../../../design_system/components/Loader.jsx";
import { indexOfObject } from "../../../../utils/ArrayUtils";
import debounce from "lodash/debounce";

const MINCHAR = 2;

export const FilterSuggestionBox = ({
    filtersState,
    filterType="include",
    filterTab,
    prop_params,
    customInputStyle,
    lens_style,
    setShowSearchedRes,
    suggestionRenderer,
    ...props }) => {
    const [fetchedRes, setFetchedRes] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [isFetching, setIsFetching] = useState(false);
    const [activeOption, setActiveOption] = useState(0);
    const [showOptions, setShowOptions] = useState(false);

    useEffect(() => {
        if (userInput.length == 0) resetAll();
        if(setShowSearchedRes) setShowSearchedRes(showOptions)
    }, [showOptions]);

    const debouncedHandleChange = useCallback(
        debounce((ip) => {
            setIsFetching(true);
            let data_params = prop_params || {}
            data_params = {...(filtersState.data_params || {}), ...data_params}
            apiAgent({
                method: "get",
                url: props.suggestionUrl,
                params: {
                    term_query: ip,
                    term: filterTab.tab_key,
                    is_custom: filterTab.is_custom,
                    ...data_params,
                },
            })
                .then((res) => {
                    setIsFetching(false);
                    setFetchedRes(res.data.aggregates);
                    setShowOptions(true);
                    if(props.filtersStateReducer) {
                        let aggregates = res.data.aggregates;
                        let mappedKey = filterTab.tab_key;
                        props.filtersStateReducer({type: "ASSIGN_SEARCHED_AGGREGATES", payload: {aggregates, filterType, mappedKey}})
                    }
                })
                .catch((e) => {
                    setIsFetching(false);
                    setShowOptions(true);
                });
        }, 300),
        [filtersState]
    );

    const dupped = () => {
        let {aggregatesData} = filtersState;
        let temp = {...aggregatesData[filterType]}
        let aggregates = [...temp[filterTab.tab_key]]
        return (
            aggregates.filter((val) => val.term == fetchedRes[activeOption].term).length > 0
        );
    };

    const resetAll = () => {
        setFetchedRes([]);
        setActiveOption(0);
        setShowOptions(false);
        setUserInput("");
    };

    const applyAlreadyExisting = () => {
        let val = fetchedRes[activeOption].term;
        let mappedKey = filterTab.tab_key;
        let { filtersSelected } = filtersState;
        let index = indexOfObject(filtersSelected[filterType][mappedKey], "value", val);
        if (index === -1) {
            let new_val = [...filtersSelected[filterType][mappedKey], {value: val}];
            props.filtersStateReducer({
                type: "SELECTED_FILTER",
                payload: {
                    mappedKey: mappedKey,
                    selections: new_val ,
                    selectedVal: val,
                    select: true,
                    filterType,
                    insertAt: "PREPEND"
                },
            });
        } else {
            let temp = [...filtersSelected[filterType][mappedKey]];
            temp.splice(index, 1);
            props.filtersStateReducer({
                type: "SELECTED_FILTER",
                payload: { mappedKey: mappedKey, selections: temp, filterType },
            });
        }
    };

    const handleOnClick = () => {
        if (!dupped())
            props.filtersStateReducer({
                type: "SELECT_AND_MODIFY",
                payload: {
                    mappedKey: filterTab.tab_key,
                    data: fetchedRes[activeOption],
                    filterType
                },
            });
        else {
            applyAlreadyExisting();
        }
        resetAll();
    };

    const handleOnChange = (e) => {
        let input = e.target.value;
        setUserInput(input);
        if (input.length >= MINCHAR) {
            debouncedHandleChange(input);
        }
        if (input.length == 0) {
            resetAll();
        }
    };

    const handleOnKeyDown = (e) => {
        if (e.keyCode === 13 && fetchedRes.length > 0) {
            if (!dupped())
                props.filtersStateReducer({
                    type: "SELECT_AND_MODIFY",
                    payload: {
                        mappedKey: filterTab.tab_key,
                        data: fetchedRes[activeOption],
                        filterType
                    },
                });
            else {
                applyAlreadyExisting();
            }
            resetAll();
        } else if (e.keyCode === 38) {
            if (activeOption === 0) return;
            setActiveOption(activeOption - 1);
        } else if (e.keyCode === 40) {
            if (activeOption === fetchedRes.length - 1) return;
            setActiveOption(activeOption + 1);
        }
    };

    const defaultRenderer = () => {
        if (!showOptions) return null;
        return (
            <ul className="suggestion-box__menu filter-suggestion__menu-list">
                {fetchedRes.length > 0 ? (
                    fetchedRes.map((optionName, index) => (
                        <li
                            key={optionName.term}
                            className={`suggestion-box__item${index === activeOption ? ' suggestion-box__item--active' : ''}`}
                            onClick={handleOnClick}
                            onMouseEnter={() => setActiveOption(index)}
                        >
                            {optionName.term}
                        </li>
                    ))
                ) : (
                    <li className="suggestion-box__item suggestion-box__item--no-results">
                        No Results
                    </li>
                )}
            </ul>
        );
    };

    const renderOptionList = () => {
        if(suggestionRenderer)
            return <suggestionRenderer.component showsearchedRes={showOptions} handleFilterCheckBoxChange={props.handleFilterCheckBoxChange}/>;
        return defaultRenderer();
    };

    return (
        <div style={props.style}>
            <ConditionalRender if={showOptions && !suggestionRenderer}>
                <div className="filter-suggestion__backdrop" onClick={resetAll} />
            </ConditionalRender>
            <ConditionalRender if={props.label}>
                <div className="body-s filter-suggestion__label">
                    {props.label}
                </div>
            </ConditionalRender>
            <ConditionalRender if={filterTab.search_bar || filterTab.search_enabled}>
                <div className="relative flex-shrink-0 filter-suggestion__search-wrap">
                    <InputBox
                        Size="Large"
                        value={userInput}
                        handleOnChange={handleOnChange}
                        handleOnKeyDown={handleOnKeyDown}
                        placeholder={props.placeholder}
                        showIcon={!!props.inputClass}
                        IconName={props.inputClass ? "search" : null}
                        error={props.error || ""}
                        inputStyle={customInputStyle}
                    />
                </div>
            </ConditionalRender>
            <div style={suggestionRenderer ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' } : { position: "relative" }}>
                <ConditionalRender if={isFetching}>
                    <div className="w-full h-full flex items-center justify-center flex-1 mt-l">
                        <Loader isLoading={true} Size="Medium" isFullPage={false}/>
                    </div>
                </ConditionalRender>
                <ConditionalRender if={!isFetching}>
                    {renderOptionList()}
                </ConditionalRender>
            </div>
        </div>
    );
};
